import { useState, useEffect, useMemo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { getMatchDownlineBets } from '../services/api'
import useModalEnter from '../hooks/useModalEnter'
import LoaderOverlay from './LoaderOverlay'
import { betNation } from '../utils/betRun'

// View More bets — reference modal (modal-big): top nav-tabs = market (Normal /
// MATCH_ODDS ...), phir card-header pills = Matched / Unmatched / Deleted. Har
// pane me search form (username / amount / ip) + radio (all/back/lay/deleted) +
// Total Soda/Amount + report-table (Username/Nation/Rate/Amount/Date/IP/Detail/
// Action checkbox). Data getMatchDownlineBets se; search/filter client-side hai
// (server-side search-bets endpoint available nahi — missing-apis.md me noted).
//
// ── EMPTY TAB (user, 2026-09-22, side-by-side screenshot) ───────────────────
// All of that is the reference's `GameBetList`, and it renders ONLY when the
// tab actually has bets:
//
//   <div>{gameSoda && gameSoda.length ? <div>…form + radios + totals…</div> : _e()}
//        {sodaList && sodaList.length ? <div class="table-responsive report-table">…</div> : _e()}</div>
//
// and the modal's tab itself is
//   betList.length > 0 ? <GameBetList/> : <div class="text-center">No records found</div>
//
// So with no bets the reference shows the three pills and that one line —
// no search form, no radios, no totals, no table head. Ours drew the whole
// apparatus around an empty table. Note the two different gates: the form
// hangs off the tab's OWN bets, the table off the SEARCHED rows, so a search
// that matches nothing keeps the form and drops the table.
//
// The loader also comes first, as everywhere else in the reference
// (`isLoading = true` → fetch → `isLoading = false` → `.show()`). Unlike the
// User Book, this modal DOES open on an empty list — that is the screenshot.
// Pure row helpers — outside the component so the memos below can depend on
// them without being rebuilt on every render.
const isDeleted = (b) => ['deleted', 'DELETED'].includes(b.status)
const statusOf = (b) => (isDeleted(b) ? 'deleted' : b.unmatched === true ? 'unmatched' : 'matched')
const marketOf = (b) => b.market_type || b.game_type || ''

function ViewMoreBetsModal({ show, onHide, eventId }) {
  const [allBets, setAllBets] = useState([])
  // 'booting' = the modal is not on screen yet (first load of this opening),
  // 'refreshing' = it IS on screen and a tab switch is reloading behind the
  // overlay. The reference uses one `isLoading` flag for both; the difference
  // is only whether the modal happens to be open already.
  const [loading, setLoading] = useState(false)
  const [booted, setBooted] = useState(false)
  const [activeMarket, setActiveMarket] = useState(null)
  const [activeTab, setActiveTab] = useState('matched') // matched | unmatched | deleted
  // what is typed in the boxes …
  const [search, setSearch] = useState({ username: '', amountFrom: '', amountTo: '', ip: '' })
  /* … and what the table is actually filtered by. The Search button is what
     moves one into the other, so typing alone changes nothing on screen
     (user, 2026-09-23: "make sure jab search pe click karte tab hi result
     aaye"). The radio is NOT part of this — the reference filters on the
     radio's own `change` handler, immediately. */
  const [applied, setApplied] = useState({ username: '', amountFrom: '', amountTo: '', ip: '' })
  /* Opens on All (user 2026-10-20: "the default All radio should be active").
     It used to open with no radio picked, per an older reference capture.
     Reset and close also go back to 'all'. */
  const [radio, setRadio] = useState('all') // all | back | lay | deleted

  /* Every tab click reloads, exactly as the reference does — its
     `getMarketBets(mid, type)` sets `isLoading = true`, calls
     `game/getmarketbets` with the market and the tab's type, and only then
     drops the flag. Ours has one endpoint for all three types and filters
     after, but the reload (and so the overlay) is real. */
  const loadBets = useCallback(() => {
    if (!eventId) return
    setLoading(true)
    getMatchDownlineBets({ eventId, limit: 500 })
      .then((res) => { if (res?.success) setAllBets(res.data || []) })
      .catch((e) => console.error('Error fetching bets:', e))
      .finally(() => { setLoading(false); setBooted(true) })
  }, [eventId])

  useEffect(() => {
    if (!show) { setBooted(false); return }
    loadBets()
  }, [show, loadBets])

  // top-level market tabs (unique market_type in order)
  const markets = useMemo(() => {
    const seen = []
    for (const b of allBets) { const m = marketOf(b); if (m && !seen.includes(m)) seen.push(m) }
    // user 2026-10-24: the market with the newest bet comes first (all sports).
    const latest = {}
    for (const b of allBets) {
      const t = new Date(b.created_at || b.createdAt || b.placed_at || b.date).getTime() || 0
      const m = marketOf(b)
      if (m && t > (latest[m] || 0)) latest[m] = t
    }
    return seen.sort((a, b) => (latest[b] || 0) - (latest[a] || 0))
  }, [allBets])

  useEffect(() => {
    if (markets.length && !markets.includes(activeMarket)) setActiveMarket(markets[0])
  }, [markets, activeMarket])

  /* The tab's OWN bets, before the search box touches them — the reference's
     `gameSoda`. It decides whether the search form / radios / totals exist at
     all; `rows` below (its `sodaList`) decides whether the table does. */
  const tabBets = useMemo(
    () => allBets.filter((b) => marketOf(b) === activeMarket && statusOf(b) === activeTab),
    [allBets, activeMarket, activeTab],
  )

  // active market + tab + search + radio filter
  const rows = useMemo(() => allBets.filter((b) => {
    if (marketOf(b) !== activeMarket) return false
    if (statusOf(b) !== activeTab) return false
    const u = String(b.username || b.user?.username || '').toLowerCase()
    if (applied.username && !u.includes(applied.username.toLowerCase())) return false
    const amt = parseFloat(b.stake_amount ?? b.amount) || 0
    if (applied.amountFrom && amt < parseFloat(applied.amountFrom)) return false
    if (applied.amountTo && amt > parseFloat(applied.amountTo)) return false
    const ip = String(b.ip_address || b.ip || '')
    if (applied.ip && !ip.includes(applied.ip)) return false
    const bt = String(b.bet_type || b.type || '').toLowerCase()
    if (radio === 'back' && !['back', 'yes'].includes(bt)) return false
    if (radio === 'lay' && !['lay', 'no'].includes(bt)) return false
    if (radio === 'deleted' && !isDeleted(b)) return false
    return true
  }), [allBets, activeMarket, activeTab, applied, radio])

  const totalSoda = rows.length
  const totalAmount = rows.reduce((s, b) => s + (parseFloat(b.stake_amount ?? b.amount) || 0), 0)

  // reopen lands on the newest market again, not the last one viewed
  const handleClose = () => { setActiveMarket(null); setActiveTab('matched'); setSearch({ username: '', amountFrom: '', amountTo: '', ip: '' }); setApplied({ username: '', amountFrom: '', amountTo: '', ip: '' }); setRadio('all'); onHide() }

  // the modal mounts only once the bets are here, so its open animation starts
  // from the loader — reference: isLoading → fetch → isLoading off → .show()
  const { modalClass, backdropClass, onBackdropClick } = useModalEnter(handleClose, show && booted)

  if (!show) return null
  // first load of this opening: only the overlay, the modal is not up yet
  if (!booted) return createPortal(<LoaderOverlay />, document.body)
  const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? String(n) : (v ?? '-') }
  /* The reference's `v-currency`, verbatim (capture index.html):
       parseFloat(value).toLocaleString("EN-IN", { maximumFractionDigits: 2 })
     It is on the Amount column and on Total Amount — which is why the reference
     reads `1,100` where ours read `1100` (user's screenshot, 2026-09-23). The
     Rate column is NOT currency-formatted there, so it stays on `num`. */
  const currency = (v) => {
    const n = parseFloat(v)
    return Number.isFinite(n) ? n.toLocaleString('EN-IN', { maximumFractionDigits: 2 }) : ''
  }
  const fmtDate = (raw) => {
    if (!raw) return ''
    const d = new Date(raw)
    if (isNaN(d)) return String(raw)
    const p = (n) => String(n).padStart(2, '0')
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
  }

  const onSearch = (e) => { e.preventDefault(); setApplied(search) }
  const onReset = () => { setSearch({ username: '', amountFrom: '', amountTo: '', ip: '' }); setApplied({ username: '', amountFrom: '', amountTo: '', ip: '' }); setRadio('all') }

  /* Nothing in this tab → the reference renders only this line (and on the
     Unmatched / Deleted tabs, nothing at all). No form, no radios, no totals,
     no table head. */
  const emptyBody = activeTab === 'matched'
    ? <div className="text-center">No records found</div>
    : null

  const paneBody = tabBets.length === 0 ? emptyBody : (
    <div>
      <div>
        <form method="post" data-vv-scope="searchBets" className="ajaxFormSubmit" onSubmit={onSearch}>
          <div className="row row5 align-items-center mb-3">
            <div className="col-xl-2">
              <label htmlFor="uname">Username</label>
              <input id="uname" type="text" placeholder="Search Username" className="form-control"
                value={search.username} onChange={(e) => setSearch((s) => ({ ...s, username: e.target.value }))} />
            </div>
            <div className="col-xl-3">
              <div className="row row5">
                <div className="col-6">
                  <label htmlFor="amountfrom">Amount From</label>
                  <input id="amountfrom" name="amountFrom" type="text" placeholder="Amount From" className="form-control"
                    value={search.amountFrom} onChange={(e) => setSearch((s) => ({ ...s, amountFrom: e.target.value }))} />
                </div>
                <div className="col-6">
                  <label htmlFor="amountto">Amount To</label>
                  <input id="amountto" name="amountto" type="text" placeholder="Amount To" className="form-control"
                    value={search.amountTo} onChange={(e) => setSearch((s) => ({ ...s, amountTo: e.target.value }))} />
                </div>
              </div>
            </div>
            <div className="col-xl-2">
              <label htmlFor="ipaddr">IP Address</label>
              <input id="ipaddr" name="ip" type="text" placeholder="IP Address" className="form-control"
                value={search.ip} onChange={(e) => setSearch((s) => ({ ...s, ip: e.target.value }))} />
            </div>
            <div className="col-xl-2 mt-4">
              <button type="submit" className="btn btn-primary">Search</button>
              {' '}
              <button type="button" className="btn btn-light" onClick={onReset}>Reset</button>
            </div>
          </div>
        </form>
        <div id="radio-group-1" role="radiogroup" tabIndex="-1" className="float-left bv-no-focus-ring">
          {[['all', 'All'], ['back', 'Back'], ['lay', 'Lay'], ['deleted', 'Deleted']].map(([v, label], i) => (
            <div key={v} className="custom-control custom-control-inline custom-radio">
              <input id={`radio-group-1_BV_option_${i}`} type="radio" name="radio-options" className="custom-control-input"
                value={v} checked={radio === v} onChange={() => setRadio(v)} />
              <label htmlFor={`radio-group-1_BV_option_${i}`} className="custom-control-label"><span>{label}</span></label>
            </div>
          ))}
        </div>
        <div className="custom-control-inline float-right">
          <h5>
            {/* reference: the total carries `text-success` at >= 0 and
                `text-danger` below it, and prints through `v-currency` */}
            Total Soda: <span className="text-success mr-2">{totalSoda}</span> Total Amount:
            {' '}<span className={Number(totalAmount) < 0 ? 'text-danger' : 'text-success'}>{currency(totalAmount)}</span>
          </h5>
        </div>
      </div>
      {/* The filtered list (`sodaList`) decides between the table and a line —
          it is never "nothing". The reference's own render:

            sodaList && sodaList.length
              ? <div class="table-responsive report-table"><table…></div>
              : <div class="table-responsive report-table text-center">No records found</div>

          and the user's DOM of it proves the empty leg: picking the Deleted
          radio on a pane with no deleted bets leaves the form, the radios and
          the totals (`Total Soda: 0  Total Amount: 0`) in place and puts that
          line underneath. Ours dropped the whole area and showed nothing
          (user, 2026-09-23: "ye scenario bhi daal exact, deleted select pe"). */}
      {rows.length === 0 ? (
        <div className="table-responsive report-table text-center">No records found</div>
      ) : (
      <div className="table-responsive report-table">
        <table className="table">
          <thead>
            <tr>
              <th className="user-name"><div>Username</div></th>
              <th className="event-name"><div>Nation</div></th>
              <th className="text-right bet-user-rate"><div>Rate</div></th>
              <th className="text-right bet-amount"><div>Amount</div></th>
              <th className="bet-date"><div>Date</div></th>
              <th><div>IP</div></th>
              <th><div>B Details</div></th>
              <th className="text-right"><div>Action</div></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((bet, i) => {
              // oddeven: Odd and Even are both backs — blue strip (reference: back-border when
              // back OR gtype oddeven, user 2026-10-24)
              const isLay = ['lay', 'no'].includes(String(bet.bet_type || bet.type || '').toLowerCase())
                && String(bet.market_type || bet.gtype || '').toLowerCase() !== 'oddeven'
              // badlo is an exact-run swap, neither back nor lay — no colour strip
              // (same rule as the player StatementResultModal, user 2026-10-25)
              const isBadlo = bet.badlo === true || String(bet.badlo) === 'true'
              return (
                <tr key={bet.id || i} className={isBadlo ? undefined : isLay ? 'lay-border' : 'back-border'}>
                  <td className="user-name"><div>{bet.username || bet.user?.username || '-'}</div></td>
                  <td className="event-name"><div>{betNation(bet)}</div></td>
                  <td className="text-right bet-user-rate"><div>{num(bet.odds)}</div></td>
                  <td className="text-right bet-amount"><div>{currency(bet.stake_amount ?? bet.amount)}</div></td>
                  <td className="bet-date"><div>{fmtDate(bet.created_at || bet.createdAt || bet.placed_at || bet.date)}</div></td>
                  <td><a href="javascript:void(0)">{bet.ip_address || bet.ip || '-'}</a></td>
                  <td><a href="javascript:void(0)" className="text-success">Detail</a></td>
                  <td className="text-right">
                    <div className="custom-control custom-checkbox">
                      <input type="checkbox" className="custom-control-input" value="0" id={`vm-chk-${bet.id || i}`} />
                      <label className="custom-control-label" htmlFor={`vm-chk-${bet.id || i}`}></label>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      )}
    </div>
  )


  /* Portaled to <body>, which is where BootstrapVue puts a `b-modal`: in the
     reference capture `#__BVID__1979___BV_modal_outer_` is a direct child of
     body, sitting after the page scripts — NOT inside `.detail-page-container`.
     That placement is what picks the font: `body { font-family: Poppins }`
     (app.css) vs `.detail-page-container { font-family: "Noto Sans" }`
     (style.css:732 — the reference has the very same rule). Rendering the modal
     in place left it inside the container, so every word in it came out in Noto
     Sans while the reference showed Poppins (user's side-by-side, 2026-09-23). */
  return createPortal(
    <div style={{ position: 'absolute', zIndex: 1040 }}>
      <div role="dialog" className={modalClass} aria-modal="true" onClick={onBackdropClick} style={{ display: 'block' }}>
        <div className="modal-dialog modal-big">
          <span tabIndex="0"></span>
          <div tabIndex="-1" className="modal-content">
            <header className="modal-header">
              <h5 className="modal-title">View More</h5>
              <button type="button" aria-label="Close" className="close" onClick={handleClose}>×</button>
            </header>
            <div className="modal-body">
              {/* top-level market tabs — only when there are markets: the
                  reference renders no (empty, 19px) ul on a match without bets
                  (ODI 638827817, 2026-10-19) */}
              {markets.length > 0 && <ul role="tablist" className="nav nav-tabs d-inline-block text-uppercase">
                {markets.map((m) => (
                  <li key={m} className="nav-item d-inline-block">
                    {/* A market tab starts the pane over: back to Matched Bets
                        with an empty search and no radio picked, then the
                        refetch (whose loader dims the modal). The reference's
                        recording never shows the pill sitting on Unmatched or
                        Deleted after a MATCH_ODDS / BOOKMAKER tap — it is always
                        Matched Bets again. The radio lands on All, same as
                        Reset (user, 2026-09-23: "reset pe aur MATCH_ODDS na
                        Bookmaker change pe automatically All pe le ke jana"). */}
                    <a data-toggle="tab" href="javascript:void(0)"
                      className={`nav-link${activeMarket === m ? ' active' : ''}`}
                      onClick={() => {
                        setActiveMarket(m)
                        setActiveTab('matched')
                        setSearch({ username: '', amountFrom: '', amountTo: '', ip: '' })
                        setApplied({ username: '', amountFrom: '', amountTo: '', ip: '' })
                        setRadio('all')
                        loadBets()
                      }}>{m}</a>
                  </li>
                ))}
              </ul>}
              <div className="tab-content m-t-20">
                <div className="tabs">
                  <div className="card-header">
                    <ul role="tablist" className="nav nav-pills card-header-pills">
                      {[['matched', 'Matched Bets'], ['unmatched', 'Unmatched Bets'], ['deleted', 'Deleted Bets']].map(([v, label]) => (
                        <li key={v} role="presentation" className="nav-item">
                          <a role="tab" href="#" target="_self"
                            className={`nav-link${activeTab === v ? ' active' : ''}`}
                            onClick={(e) => { e.preventDefault(); setActiveTab(v); loadBets() }}>{label}</a>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="tab-content">
                    {/* reference (BootstrapVue b-tab): `div[role=tabpanel].tab-pane.active.card-body`,
                        and on Matched an inner `#matched-bet2.tab-pane.active` */}
                    <div role="tabpanel" className="tab-pane active card-body">
                      {activeTab === 'matched'
                        ? <div id="matched-bet2" className="tab-pane active">{paneBody}</div>
                        : paneBody}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <span tabIndex="0"></span>
        </div>
      </div>
      <div className={backdropClass}></div>
      {/* tab switch: the modal stays up and the overlay dims the whole page
          on top of it (user's recording, 2026-09-22) */}
      {loading && createPortal(<LoaderOverlay />, document.body)}
    </div>,
    document.body,
  )
}

export default ViewMoreBetsModal
